import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Header from "../Header";
import Field from "../../../../../Settings/Content/Components/Field";
import Tooltip from "../../../../../../Components/common/Tooltip/Tooltip";
import Backdrop from "../../../../../../Components/UI/Backdrop/Backdrop";

import { RequestUpdateAgentPolicy } from "../../../../../../store/actions/integrationsActions";

const MIN_MAX_VALUES = {
  minFileSize: [1500, 4500],
  maxFileSize: [15800000, 47400000],
  memMinFileSize: [12500, 37500],
  memMaxFileSize: [30300000, 90900000],
  maxAvScanSize: [4450000, 13350000],
};

const SCALAR_FIELDS = [
  "heuristicMacros",
  "heuristicEncryptedDoc",
  "heuristicEncryptedArchive",
  "minFileSize",
  "maxFileSize",
  "memMinFileSize",
  "memMaxFileSize",
  "maxAvScanSize",
];
const BOOL_FIELDS = ["heuristicMacros", "heuristicEncryptedDoc", "heuristicEncryptedArchive"];

const toBool = (v) => v === true || v === "true" || v === "True";

const AVForm = ({ selectedHost }) => {
  const dispatch = useDispatch();

  const policy = useSelector((state) => state.integrations.agentPolicy);
  const updatePolicyLoading = useSelector((state) => state.integrations.updatePolicyLoading);

  const backend = policy?.settings?.av;

  const [avUI, setAvUI] = useState({
    ...backend,
    heuristicMacros: toBool(backend?.heuristicMacros),
    heuristicEncryptedDoc: toBool(backend?.heuristicEncryptedDoc),
    heuristicEncryptedArchive: toBool(backend?.heuristicEncryptedArchive),
  });

  const numberChangeHandler = (e) => {
    const { name, value } = e.target;
    setAvUI((prev) => ({ ...prev, [name]: value === "" ? "" : Number(value) }));
  };

  const boolChangeHandler = (e) => {
    const { name, value } = e.target;
    setAvUI((prev) => ({ ...prev, [name]: value === "true" }));
  };

  const blurHandler = (e) => {
    const { name, value } = e.target;
    const limits = MIN_MAX_VALUES[name];
    if (!limits) return;

    const [min, max] = limits;
    let newValue = Number(value);

    if (newValue < min) newValue = min;
    if (newValue > max) newValue = max;

    setAvUI((prev) => ({ ...prev, [name]: newValue }));
  };

  const checkSaveButtonDisabled = () => {
    if (!backend || !avUI) return true;

    for (const f of SCALAR_FIELDS) {
      if (BOOL_FIELDS.includes(f)) {
        if (toBool(backend[f]) !== avUI[f]) return false;
      } else if (backend[f] !== avUI[f]) {
        return false;
      }
    }

    return true;
  };

  const updateAgentSettingsHandler = (e) => {
    e.preventDefault();
    if (updatePolicyLoading) return;

    dispatch(
      RequestUpdateAgentPolicy({
        id: policy.id,
        settings: {
          ...policy.settings,
          av: avUI,
        },
        host: selectedHost,
      }),
    );
  };

  const toggleStatusHandler = () => {
    dispatch(
      RequestUpdateAgentPolicy({
        id: policy.id,
        settings: {
          ...policy.settings,
          av: {
            ...policy.settings.av,
            enabled: !policy?.settings?.av?.enabled,
          },
        },
        host: selectedHost,
      }),
    );
  };

  const renderBool = (name, header, tooltip) => (
    <div className="agent-policy-form__line">
      <div className="mailing__content__security m-b-20">
        <p className="light m-b-10 mailing__content__security__header">
          {header}
          <Tooltip text={tooltip} />
        </p>
        <div className="mailing__content__security__labels">
          <label className="align-items">
            <input
              className="mailing__content__security__input"
              type="radio"
              value={true}
              name={name}
              checked={avUI[name] === true}
              onChange={boolChangeHandler}
            />
            <p className="margin-left-12">Enabled</p>
          </label>
          <label className="align-items">
            <input
              className="mailing__content__security__input"
              type="radio"
              value={false}
              name={name}
              checked={avUI[name] === false}
              onChange={boolChangeHandler}
            />
            <p className="margin-left-12">Disabled</p>
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Header
        policy={policy}
        selectedHost={selectedHost}
        toggleProps={{
          show: true,
          text: "AV Status",
          active: policy?.settings?.av?.enabled,
          onClick: toggleStatusHandler,
        }}
      />

      <form className="agent-policy-form">
        {renderBool(
          "heuristicMacros",
          "Heuristic Macros",
          "Flag documents that contain macros as suspicious.",
        )}

        {renderBool(
          "heuristicEncryptedDoc",
          "Heuristic Encrypted Doc",
          "Flag encrypted documents as suspicious.",
        )}

        {renderBool(
          "heuristicEncryptedArchive",
          "Heuristic Encrypted Archive",
          "Flag encrypted archives as suspicious.",
        )}

        <div className="agent-policy-form__line">
          <Field
            label="Min File Size in bytes"
            tooltipText="Min: 1500, Max: 4500"
            width="220px"
            name="minFileSize"
            value={avUI.minFileSize}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
          <Field
            label="Max File Size in bytes"
            tooltipText="Min: 15800000, Max: 47400000"
            width="220px"
            name="maxFileSize"
            value={avUI.maxFileSize}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
        </div>

        <div className="agent-policy-form__line">
          <Field
            label="Memory Min File Size in bytes"
            tooltipText="Min: 12500, Max: 37500"
            width="220px"
            name="memMinFileSize"
            value={avUI.memMinFileSize}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
          <Field
            label="Memory Max File Size in bytes"
            tooltipText="Min: 30300000, Max: 90900000"
            width="220px"
            name="memMaxFileSize"
            value={avUI.memMaxFileSize}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
        </div>

        <div className="agent-policy-form__line">
          <Field
            label="Max AV Scan Size in bytes"
            tooltipText="On-access cap. Min: 4450000, Max: 13350000"
            width="220px"
            name="maxAvScanSize"
            value={avUI.maxAvScanSize}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
        </div>

        <button
          className="primary-button"
          disabled={checkSaveButtonDisabled()}
          onClick={updateAgentSettingsHandler}>
          Save
        </button>

        <Backdrop
          spinner={false}
          absolute
          show={!policy?.settings?.av?.enabled}
          message="AV settings are inactive! Please click on the status button at the header to activate av settings."
          messageStyle={{ paddingTop: 0 }}
        />
      </form>
    </>
  );
};

export default AVForm;
